import { autoDetectHotspot } from "./autoDetectHotspot"
import { FrameBuilder } from "./FrameBuilder"
import { FrameDelay } from "./FrameDelay"
import { SourceError } from "./SourceLoader.errors"
import { commonDelay, sortByName, validateEntryFile } from "./SourceLoader.helpers"
import { ImageFile, stripExtension } from "../../shared/services"
import { MAX_FRAMES } from "../config"

import type { Frame } from "../types/Frame"
import type { Source } from "../types/Source"

// Decompression-bomb guard: checked on the header size, before anything decodes the pixels.
const MAX_SIDE = 8192
// An SVG without width/height attributes has no intrinsic size.
const FALLBACK_SIDE = 512
const DEFAULT_DELAY_MS = 150
// Browsers show an animation frame that has no usable delay of its own for 100 ms.
const UNTIMED_FRAME_DELAY_MS = 100
// Past this ratio of the long side to the short one, Fit leaves wide empty bands.
const OBLONG_RATIO = 1.3

const isOblong = (width: number, height: number) =>
  Math.max(width, height) / Math.min(width, height) > OBLONG_RATIO

// Turns picked files into grid-sized frames. Throws a SourceError with a message safe to show.
export class SourceLoader {
  static async load(files: File[]): Promise<Source> {
    const kept = sortByName(files).slice(0, MAX_FRAMES)
    this.#assertReadable(kept)
    const [first, ...rest] = kept
    const isLone = rest.length === 0
    const type = ImageFile.sniffType(first)
    // The header check runs first, so an oversized image never reaches ImageDecoder.
    const { image, width, height } = await this.#loadCheckedImage(first, type)
    const animation = isLone ? await this.#decodeAnimation(first, type) : null
    const frames = animation?.frames.length
      ? animation.frames
      : [
          await FrameBuilder.build(image, width, height, type, first.name),
          ...(await this.#loadStills(rest)),
        ]

    return {
      // The id is only a React key. Not crypto.randomUUID: that needs a secure context.
      id: Math.random().toString(36).slice(2),
      name: isLone ? first.name : `${stripExtension(first.name)} +${rest.length} more`,
      filename: first.name,
      type,
      width,
      height,
      bytes: kept.reduce((sum, file) => sum + file.size, 0),
      frames,
      delayMs: animation?.delayMs ?? DEFAULT_DELAY_MS,
      droppedFrames: files.length - kept.length + (animation?.droppedFrames ?? 0),
      isFirstFrameOnly: isLone && !animation && (await ImageFile.holdsAnimation(first, type)),
      autoResampling: FrameBuilder.autoResampling(width, height, type),
      hotspot: autoDetectHotspot(frames[0].pixels),
      suggestions: {
        ...(frames[0].sourceBackground && { removeBackground: true }),
        ...(isOblong(width, height) && { framing: "fill" }),
      },
    }
  }

  // Each file's first frame, in name order, for as many files as there is room for.
  static async loadFrames(files: File[], room: number) {
    const kept = sortByName(files).slice(0, room)
    this.#assertReadable(kept)
    return this.#loadStills(kept)
  }

  static #assertReadable(files: File[]) {
    for (const file of files) {
      const problem = validateEntryFile(file, ImageFile.sniffType(file))
      if (problem) throw new SourceError(problem)
    }
  }

  static async #loadCheckedImage(file: File, type: string) {
    const image = await ImageFile.decode(file, type)
    const { naturalWidth, naturalHeight } = image
    if (naturalWidth > MAX_SIDE || naturalHeight > MAX_SIDE) {
      throw new SourceError(
        `“${file.name}” is ${naturalWidth}×${naturalHeight} px — the limit is ${MAX_SIDE} px per side.`,
      )
    }
    const hasSize = naturalWidth > 0 && naturalHeight > 0

    return {
      image,
      width: hasSize ? naturalWidth : FALLBACK_SIDE,
      height: hasSize ? naturalHeight : FALLBACK_SIDE,
    }
  }

  // One file at a time, each as its first frame.
  static async #loadStills(files: File[]) {
    const frames: Frame[] = []
    for (const file of files) {
      const type = ImageFile.sniffType(file)
      const { image, width, height } = await this.#loadCheckedImage(file, type)
      frames.push(await FrameBuilder.build(image, width, height, type, file.name))
    }
    return frames
  }

  // Null when this browser can't read the frames. No frames means a still: the file holds a single
  // frame, or it failed to decode and only an <img> can still show it.
  static async #decodeAnimation(file: File, type: string) {
    const decoderType = ImageFile.decoderTypeFor(type)
    if (!decoderType || !("ImageDecoder" in window)) return null
    if (!(await ImageDecoder.isTypeSupported(decoderType))) return null

    const frames: Frame[] = []
    const delays: number[] = []
    let decoder: ImageDecoder | undefined
    try {
      decoder = new ImageDecoder({
        data: await file.arrayBuffer(),
        type: decoderType,
        preferAnimation: true,
      })
      await decoder.tracks.ready
      // The frame count is final only once the whole file is buffered.
      await decoder.completed
      const total = decoder.tracks.selectedTrack?.frameCount ?? 1
      if (total < 2) return { frames, delayMs: DEFAULT_DELAY_MS, droppedFrames: 0 }

      const count = Math.min(total, MAX_FRAMES)
      // One full-size frame alive at a time: each is reduced to its master before the next decodes.
      for (let index = 0; index < count; index += 1) {
        const { image } = await decoder.decode({ frameIndex: index })
        let bitmap: ImageBitmap
        try {
          const delay = Math.round((image.duration ?? 0) / 1000)
          delays.push(delay < FrameDelay.MIN_MS ? UNTIMED_FRAME_DELAY_MS : delay)
          // pica takes an ImageBitmap, not a VideoFrame.
          bitmap = await createImageBitmap(image)
        } finally {
          image.close()
        }
        try {
          const name = `${file.name}, frame ${index + 1}`
          frames.push(await FrameBuilder.build(bitmap, bitmap.width, bitmap.height, type, name))
        } finally {
          bitmap.close()
        }
      }

      return {
        frames,
        delayMs: commonDelay(delays, DEFAULT_DELAY_MS),
        droppedFrames: total - count,
      }
    } catch {
      return { frames: [], delayMs: DEFAULT_DELAY_MS, droppedFrames: 0 }
    } finally {
      decoder?.close()
    }
  }
}
